import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  X, Loader2, Send, Sparkles, ChevronRight, ChevronLeft,
  CheckCircle2, AlertTriangle, Wand2, Ban, RefreshCw,
} from "lucide-react";

/**
 * Send Campaign Dialog — v4 spec §6 flow.
 *
 * Step 1 · Pick a registered active campaign
 * Step 2 · Segment builder (bands / quiet-days / tags / score range) + live count
 * Step 3 · Context + per-variable guidance (with exact-field mode toggle + Auto-map)
 * Step 4 · 3 real rendered previews (Re-roll)
 * Step 5 · Confirm & send
 */
export default function SendCampaignDialog({ open, onClose, initial }) {
  const [step, setStep] = useState(1);
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [segment, setSegment] = useState({ bands: [], tags: [], quiet_days_min: null, quiet_days_max: null, score_min: null, score_max: null });
  const [availableTags, setAvailableTags] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [context, setContext] = useState("");
  const [variables, setVariables] = useState([]); // [{guidance, use_exact_field, field_name, fallback}]
  const [previews, setPreviews] = useState([]);
  const [fieldDefs, setFieldDefs] = useState({ standard: [], custom: [] });
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);
  const pollRef = useRef(null);

  const reset = useCallback(() => {
    setStep(1); setCampaign(null);
    setSegment({ bands: [], tags: [], quiet_days_min: null, quiet_days_max: null, score_min: null, score_max: null });
    setEligible([]); setExcluded([]); setSelectedIds([]); setContext(""); setVariables([]);
    setPreviews([]); setBatch(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    // Load campaigns + field defs
    Promise.all([
      api.get("/campaigns").catch(() => ({ data: [] })),
      api.get("/employz/field-defs").catch(() => ({ data: { standard: [], custom: [] } })),
      api.get("/employz/contacts-table").catch(() => ({ data: { contacts: [] } })),
    ]).then(([cs, fs, ct]) => {
      const active = (cs.data || []).filter((c) => c.status === "active");
      setCampaigns(active);
      setFieldDefs(fs.data || { standard: [], custom: [] });
      // Compute tag pool
      const bag = new Set();
      (ct.data?.contacts || []).forEach((c) => (c.tags || []).forEach((t) => bag.add(t)));
      setAvailableTags([...bag].sort());
      // If launched from an autopilot proposal, prefill
      if (initial?.campaign_id) {
        const c = active.find((x) => x.id === initial.campaign_id);
        if (c) {
          setCampaign(c);
          setVariables(Array.from({ length: c.param_count || 0 }, (_, i) => ({
            guidance: initial?.guidance?.[i] || "",
            use_exact_field: false, field_name: null, fallback: "",
          })));
          setContext(initial?.context || "");
          if (initial?.segment) setSegment(initial.segment);
          setStep(2);
        }
      }
    });
  }, [open, initial, reset]);

  // Segment preview (live count) whenever segment changes on step 2
  const refreshSegment = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post("/campaigns/preview-segment", { segment });
      setEligible(r.data.eligible || []);
      setExcluded(r.data.excluded || []);
      setSelectedIds((r.data.eligible || []).map((c) => c.ghl_contact_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally { setBusy(false); }
  }, [segment]);

  useEffect(() => {
    if (open && step === 2 && campaign) refreshSegment();
  }, [step, open, segment.bands, segment.tags, segment.quiet_days_min, segment.quiet_days_max, segment.score_min, segment.score_max, campaign, refreshSegment]);

  const suggestGuidance = async () => {
    setBusy(true);
    try {
      const r = await api.post("/campaigns/suggest-guidance", { campaign_id: campaign.id, context });
      const g = r.data.guidance || [];
      setVariables((prev) => prev.map((v, i) => ({ ...v, guidance: g[i] ?? v.guidance })));
      toast.success("Guidance drafted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion failed.");
    } finally { setBusy(false); }
  };

  const autoMap = async () => {
    setBusy(true);
    try {
      const r = await api.post("/campaigns/auto-map-variables", { campaign_id: campaign.id });
      const map = r.data.map || [];
      setVariables((prev) => prev.map((v, i) => {
        const m = map[i] || {};
        return {
          ...v,
          use_exact_field: !!m.use_exact_field,
          field_name: m.field_name || null,
        };
      }));
      toast.success("Suggested field mapping applied.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-map failed.");
    } finally { setBusy(false); }
  };

  const renderPreviews = async () => {
    setBusy(true);
    setPreviews([]);
    try {
      const r = await api.post("/campaigns/render-previews", {
        campaign_id: campaign.id,
        context,
        variables,
        sample_contact_ids: selectedIds.slice(0, 3),
      });
      setPreviews(r.data.previews || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally { setBusy(false); }
  };

  const startSend = async () => {
    setBusy(true);
    try {
      const r = await api.post("/campaigns/send", {
        campaign_id: campaign.id,
        contact_ids: selectedIds,
        context,
        variables,
        segment,
        autopilot_proposal_id: initial?.autopilot_proposal_id || null,
      });
      // Poll the batch
      const bres = await api.get(`/reactivation/batches/${r.data.batch_id}`);
      setBatch(bres.data);
      setStep(6);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (step !== 6 || !batch?.id) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/reactivation/batches/${batch.id}`);
        setBatch(r.data);
        if (["done", "error", "cancelled"].includes(r.data.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
        }
      } catch { /* silent */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [step, batch?.id]);

  const renderedTemplateWithSlot = (i, val) => {
    if (!campaign?.template_text) return val;
    return campaign.template_text.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, "g"), val || `{{${i + 1}}}`);
  };

  const toggleBand = (b) => setSegment((s) => ({ ...s, bands: s.bands.includes(b) ? s.bands.filter((x) => x !== b) : [...s.bands, b] }));
  const toggleTag = (t) => setSegment((s) => ({ ...s, tags: s.tags.includes(t) ? s.tags.filter((x) => x !== t) : [...s.tags, t] }));

  if (!open) return null;

  const allFields = [...(fieldDefs.standard || []), ...(fieldDefs.custom || [])];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="send-campaign-overlay"
      onClick={(e) => { if (e.target === e.currentTarget && step !== 6) onClose(); }}>
      <div className="w-full max-w-4xl max-h-[92vh] rounded-2xl border border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden" data-testid="send-campaign-dialog">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-900">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400">// Send Campaign · Step {step}/6</div>
            <h2 className="font-display font-black text-2xl text-white tracking-tight mt-1">
              {campaign?.label || "Choose a campaign"}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-200" data-testid="send-campaign-close-btn"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {/* STEP 1: pick campaign */}
          {step === 1 && (
            <div className="space-y-3" data-testid="step-pick-campaign">
              {campaigns.length === 0 ? (
                <div className="p-6 rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-200 text-sm">
                  No active campaigns yet. Go to the <b>Campaigns</b> tab and register one — the wizard&apos;s mandatory test send flips it to active.
                </div>
              ) : campaigns.map((c) => (
                <button
                  key={c.id} onClick={() => { setCampaign(c); setVariables(Array.from({ length: c.param_count || 0 }, () => ({ guidance: "", use_exact_field: false, field_name: null, fallback: "" }))); setStep(2); }}
                  data-testid={`pick-campaign-${c.id}`}
                  className="w-full text-left rounded-lg border border-zinc-900 hover:border-emerald-500/40 bg-zinc-900/40 hover:bg-emerald-500/5 p-4 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-white font-medium">{c.label}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{c.campaign_name} · {c.param_count} variable{c.param_count === 1 ? "" : "s"}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-2 font-mono line-clamp-2">{c.template_text}</div>
                </button>
              ))}
            </div>
          )}

          {/* STEP 2: segment */}
          {step === 2 && campaign && (
            <div className="space-y-4" data-testid="step-segment">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Bands</div>
                <div className="flex flex-wrap gap-2">
                  {["hot", "warm", "cool", "cold"].map((b) => (
                    <button key={b} onClick={() => toggleBand(b)} data-testid={`seg-band-${b}`}
                      className={`text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border ${segment.bands.includes(b) ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200" : "border-zinc-800 text-zinc-500"}`}>{b}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Quiet for at least (days)</label>
                  <input type="number" min="0" data-testid="seg-quiet-min" value={segment.quiet_days_min ?? ""} onChange={(e) => setSegment({ ...segment, quiet_days_min: e.target.value ? parseInt(e.target.value, 10) : null })} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white" placeholder="e.g. 30" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">…and at most (days)</label>
                  <input type="number" min="0" data-testid="seg-quiet-max" value={segment.quiet_days_max ?? ""} onChange={(e) => setSegment({ ...segment, quiet_days_max: e.target.value ? parseInt(e.target.value, 10) : null })} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white" placeholder="e.g. 60" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Score ≥</label>
                  <input type="number" data-testid="seg-score-min" value={segment.score_min ?? ""} onChange={(e) => setSegment({ ...segment, score_min: e.target.value ? parseInt(e.target.value, 10) : null })} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Score ≤</label>
                  <input type="number" data-testid="seg-score-max" value={segment.score_max ?? ""} onChange={(e) => setSegment({ ...segment, score_max: e.target.value ? parseInt(e.target.value, 10) : null })} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white" />
                </div>
              </div>
              {availableTags.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Tags (any of)</div>
                  <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                    {availableTags.map((t) => (
                      <button key={t} onClick={() => toggleTag(t)} data-testid={`seg-tag-${t}`}
                        className={`text-[10px] tracking-wide px-2 py-1 rounded border ${segment.tags.includes(t) ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200" : "border-zinc-800 text-zinc-500"}`}>{t}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-zinc-900 bg-zinc-900/40 p-3 text-sm flex flex-wrap gap-x-4 gap-y-1" data-testid="seg-summary">
                <span><span className="text-emerald-300 font-display font-black text-lg">{eligible.length}</span> eligible</span>
                <span className="text-zinc-600">·</span>
                <span><span className="text-zinc-400 font-display font-black text-lg">{excluded.length}</span> auto-excluded</span>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500 ml-auto" />}
              </div>
              {excluded.length > 0 && (
                <details className="text-xs text-zinc-500">
                  <summary className="cursor-pointer">Why {excluded.length} contacts are excluded</summary>
                  <ul className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {excluded.slice(0, 30).map((c) => (
                      <li key={c.ghl_contact_id + c.reason} className="flex justify-between">
                        <span className="truncate">{c.name}</span><span className="text-zinc-600 ml-3">{c.reason}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* STEP 3: context + guidance + exact-field */}
          {step === 3 && campaign && (
            <div className="space-y-4" data-testid="step-guidance">
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Campaign context (one line describing purpose)</label>
                </div>
                <textarea data-testid="ctx-input" value={context} onChange={(e) => setContext(e.target.value)} rows={2} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white" placeholder="10% Diwali offer till Nov 5. Warm reactivation, no pushy sales." />
              </div>
              <div className="rounded-lg bg-zinc-900/40 border border-zinc-900 p-3 text-[11px] text-zinc-400 font-mono">
                {campaign.template_text}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={autoMap} disabled={busy} data-testid="btn-automap" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-emerald-500/30 text-emerald-300 rounded"><Wand2 className="w-3 h-3" /> Auto-map fields</button>
                <button onClick={suggestGuidance} disabled={busy} data-testid="btn-suggest-guidance" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-emerald-500/30 text-emerald-300 rounded"><Sparkles className="w-3 h-3" /> Suggest guidance</button>
              </div>
              <div className="space-y-3">
                {variables.map((v, i) => (
                  <div key={i} className="rounded-lg border border-zinc-900 bg-zinc-950 p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-1">{`{{${i + 1}}}`}</div>
                    <input data-testid={`var-guidance-${i + 1}`} placeholder="Guidance — describe the value shape" value={v.guidance} onChange={(e) => { const c = [...variables]; c[i] = { ...v, guidance: e.target.value }; setVariables(c); }} className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white" />
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500">
                      <input type="checkbox" checked={v.use_exact_field} onChange={(e) => { const c = [...variables]; c[i] = { ...v, use_exact_field: e.target.checked }; setVariables(c); }} data-testid={`var-exact-toggle-${i + 1}`} className="w-3.5 h-3.5 accent-emerald-500" />
                      <span>Use exact CRM field</span>
                      {v.use_exact_field && (
                        <>
                          <select data-testid={`var-exact-field-${i + 1}`} value={v.field_name || ""} onChange={(e) => { const c = [...variables]; c[i] = { ...v, field_name: e.target.value || null }; setVariables(c); }} className="ml-2 bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1 text-xs text-white">
                            <option value="">— pick a field —</option>
                            {allFields.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
                          </select>
                          <input data-testid={`var-fallback-${i + 1}`} value={v.fallback} onChange={(e) => { const c = [...variables]; c[i] = { ...v, fallback: e.target.value }; setVariables(c); }} placeholder="Fallback if empty (required)" className="ml-2 bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1 text-xs text-white flex-1" />
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: previews */}
          {step === 4 && campaign && (
            <div className="space-y-3" data-testid="step-previews">
              <div className="flex justify-between items-center">
                <div className="text-sm text-zinc-300">3 real-recipient renders — hit Re-roll to sample different contacts.</div>
                <button onClick={renderPreviews} disabled={busy} data-testid="btn-reroll-previews" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-zinc-800 text-zinc-300 rounded"><RefreshCw className="w-3 h-3" /> Re-roll</button>
              </div>
              {busy && <div className="text-center py-6 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Rendering…</div>}
              <div className="space-y-3">
                {previews.map((p, i) => (
                  <div key={i} data-testid={`preview-${i}`} className="rounded-lg border border-zinc-900 bg-zinc-900/30 p-3">
                    <div className="text-[11px] text-zinc-500 mb-2">{p.name} · {p.phone}</div>
                    {p.error ? (
                      <div className="text-rose-300 text-xs"><AlertTriangle className="w-3 h-3 inline mr-1" />{p.error}</div>
                    ) : (
                      <div className="text-sm text-emerald-100 whitespace-pre-wrap">{p.rendered}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 5: confirm */}
          {step === 5 && campaign && (
            <div className="space-y-4" data-testid="step-confirm">
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                <div className="text-sm text-emerald-200">Ready to send:</div>
                <ul className="mt-2 text-xs text-zinc-300 space-y-1">
                  <li>Campaign: <b>{campaign.label}</b> ({campaign.campaign_name})</li>
                  <li>Recipients: <b>{selectedIds.length}</b></li>
                  <li>Excluded automatically: {excluded.length}</li>
                  <li>Send pace: 1 msg every 2s</li>
                </ul>
              </div>
              <div className="text-[11px] text-zinc-500">Each message is rendered per-recipient by AI; retry once, skip on second failure. Nothing sends half-rendered.</div>
            </div>
          )}

          {/* STEP 6: running */}
          {step === 6 && batch && (
            <div className="space-y-4" data-testid="step-running">
              <div className={`rounded-xl p-4 border ${batch.status === "done" ? "border-emerald-500/30 bg-emerald-500/5" : batch.status === "cancelled" ? "border-amber-500/30 bg-amber-500/5" : batch.status === "error" ? "border-rose-500/30 bg-rose-500/5" : "border-sky-500/30 bg-sky-500/5"}`}>
                <div className="text-sm text-white capitalize flex items-center gap-2">
                  {batch.status === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : batch.status === "cancelled" ? <Ban className="w-4 h-4 text-amber-300" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                  {batch.status} · {batch.done}/{batch.total} processed
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-zinc-800"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${batch.total ? (batch.done / batch.total) * 100 : 0}%` }} /></div>
                <div className="grid grid-cols-3 gap-3 text-center mt-3">
                  <div className="rounded border border-zinc-900 py-2"><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Sent</div><div className="text-emerald-300 font-display font-black text-xl" data-testid="run-sent">{batch.sent_count || 0}</div></div>
                  <div className="rounded border border-zinc-900 py-2"><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Failed</div><div className="text-rose-300 font-display font-black text-xl" data-testid="run-failed">{batch.failed_count || 0}</div></div>
                  <div className="rounded border border-zinc-900 py-2"><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Skipped</div><div className="text-amber-300 font-display font-black text-xl" data-testid="run-skipped">{(batch.skipped || []).length}</div></div>
                </div>
              </div>
              {(batch.sent || []).slice(0, 5).map((s, i) => (
                <div key={i} className="rounded border border-zinc-900 p-2 text-[11px] text-zinc-400"><b>Sent →</b> {s.ghl_contact_id} · <span className="text-emerald-200">{s.rendered?.slice(0, 100)}…</span></div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-zinc-900 flex items-center justify-between gap-3">
          <button onClick={step > 1 && step < 6 ? () => setStep((s) => Math.max(1, s - 1)) : onClose}
            data-testid="btn-back-or-cancel"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-zinc-800 text-zinc-400 rounded">
            {step === 1 || step === 6 ? "Close" : <><ChevronLeft className="w-3.5 h-3.5" /> Back</>}
          </button>
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => setStep(3)} disabled={selectedIds.length === 0} data-testid="btn-next-2" className="text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded disabled:opacity-40">Next: guidance</button>
            )}
            {step === 3 && (
              <button onClick={() => { setStep(4); renderPreviews(); }} disabled={variables.some((v) => v.use_exact_field && !v.field_name)} data-testid="btn-next-3" className="text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded disabled:opacity-40">Preview 3 renders</button>
            )}
            {step === 4 && (
              <button onClick={() => setStep(5)} disabled={busy || previews.length === 0 || previews.every((p) => p.error)} data-testid="btn-next-4" className="text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded disabled:opacity-40">Confirm</button>
            )}
            {step === 5 && (
              <button onClick={startSend} disabled={busy} data-testid="btn-send" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-5 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded disabled:opacity-40">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send to {selectedIds.length}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
